import TwilioVoiceClient from 'dashboard/api/channel/voice/twilioVoiceClient';
import { cleanupWhatsappSession } from 'dashboard/composables/useWhatsappCallSession';
import { VOICE_CALL_TERMINAL_STATUSES } from 'dashboard/components-next/message/constants';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import buzzerBridgeSipClient from 'dashboard/services/buzzerBridgeSipClientInstance';

export const state = {
  records: [],
  callDuration: 0,
  isJoining: false,
};

export const getters = {
  getCalls: $state => $state.records,
  getActiveCall: $state => $state.records.find(call => call.isActive) || null,
  hasActiveCall: $state => $state.records.some(call => call.isActive),
  getIncomingCalls: $state => $state.records.filter(call => !call.isActive),
  hasIncomingCall: $state => $state.records.some(call => !call.isActive),
  getCallDuration: $state => $state.callDuration,
  getIsJoining: $state => $state.isJoining,
};

export const mutations = {
  ADD_CALL($state, callData) {
    const existing = $state.records.find(
      call => call.callSid === callData.callSid
    );
    if (existing) {
      const next = { ...callData };
      if (existing.caller && !next.caller) delete next.caller;
      Object.assign(existing, next, { isActive: existing.isActive });
      return;
    }

    $state.records.unshift({ ...callData, isActive: false });
  },
  REMOVE_CALL($state, callSid) {
    $state.records = $state.records.filter(call => call.callSid !== callSid);
  },
  SET_CALL_ACTIVE($state, callSid) {
    $state.records = $state.records.map(call => ({
      ...call,
      isActive: call.callSid === callSid,
    }));
  },
  CLEAR_ACTIVE_CALL($state) {
    $state.records = $state.records.filter(call => !call.isActive);
  },
  REMOVE_CALLS_FOR_CONVERSATION($state, conversationId) {
    $state.records = $state.records.filter(
      call => call.conversationId !== conversationId
    );
  },
  SET_CALL_DURATION($state, duration) {
    $state.callDuration = duration;
  },
  SET_IS_JOINING($state, isJoining) {
    $state.isJoining = isJoining;
  },
};

const teardownByProvider = call => {
  if (call?.provider === VOICE_CALL_PROVIDERS.WHATSAPP) {
    cleanupWhatsappSession();
  } else if (call?.provider === VOICE_CALL_PROVIDERS.BRIDGE) {
    buzzerBridgeSipClient.endCall();
  } else {
    TwilioVoiceClient.endClientCall();
  }
};

export const actions = {
  handleCallStatusChanged(
    { dispatch, state: currentState },
    { callSid, status }
  ) {
    if (!VOICE_CALL_TERMINAL_STATUSES.includes(status)) return;

    const call = currentState.records.find(item => item.callSid === callSid);
    if (call?.provider === VOICE_CALL_PROVIDERS.WHATSAPP) {
      dispatch('dismissCall', callSid);
      return;
    }

    dispatch('removeCall', callSid);
  },
  addCall({ commit }, callData) {
    if (callData?.callSid) commit('ADD_CALL', callData);
  },
  removeCall({ commit, state: currentState }, callSid) {
    const call = currentState.records.find(item => item.callSid === callSid);
    if (call?.isActive) teardownByProvider(call);
    commit('REMOVE_CALL', callSid);
  },
  setCallActive({ commit }, callSid) {
    commit('SET_CALL_ACTIVE', callSid);
  },
  clearActiveCall({ commit, getters: currentGetters }) {
    teardownByProvider(currentGetters.getActiveCall);
    commit('CLEAR_ACTIVE_CALL');
  },
  dismissCall({ commit }, callSid) {
    commit('REMOVE_CALL', callSid);
  },
  removeCallsForConversation({ commit, state: currentState }, conversationId) {
    currentState.records
      .filter(call => call.conversationId === conversationId && call.isActive)
      .forEach(teardownByProvider);
    commit('REMOVE_CALLS_FOR_CONVERSATION', conversationId);
  },
  setCallDuration({ commit }, duration) {
    commit('SET_CALL_DURATION', duration);
  },
  setIsJoining({ commit }, isJoining) {
    commit('SET_IS_JOINING', isJoining);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
